import React, { useState, useCallback } from "react";
import { useColors } from "../constants/Colors";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface FlatLocationData {
  display_name?: string;
  address?: {
    city?: string;
  };
  currency?: string;
}

interface FlatTextData {
  description?: string;
}

interface FlatRoomData {
  room_id?: number;
  rent?: number | string | null;
  utilities?: number | string | null;
  deposit?: number | string | null;
  size?: number | string | null;
  furnishing?: string;
  permanent?: boolean;
  from_date?: string | null;
  to_date?: string | null;
}

interface FlatCardData {
  flat_id: number;
  name: string;
  location?: string | null;
  member_count?: number;
  size?: number | string | null;
  room?: FlatRoomData[];
  flat_text?: string | FlatTextData | null;
  public?: boolean;
}

interface ActionConfig {
  label: string;
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  primary?: boolean;
}

interface FlatDetailCardProps {
  flat: FlatCardData;
  images: string[];
  room?: FlatRoomData;
  actions: ActionConfig[];
  isPrivate?: boolean;
  statusBadge?: React.ReactNode;
}

const DetailRow = ({
  icon,
  label,
  value,
  colors,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  colors: ReturnType<typeof useColors>;
}) => (
  <div className="flex items-center gap-2.5">
    <div
      className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
      style={{ backgroundColor: "rgba(2, 2, 16, 0.04)" }}
    >
      {icon}
    </div>
    <div className="min-w-0 flex flex-col justify-center">
      <p
        className="text-[11px] m-0 leading-none"
        style={{ color: colors.textNeutralMuted }}
      >
        {label}
      </p>
      <p
        className="text-sm font-medium truncate m-0 leading-none mt-1.5"
        style={{ color: colors.textNeutralDefault }}
      >
        {value}
      </p>
    </div>
  </div>
);

const FlatDetailCard: React.FC<FlatDetailCardProps> = ({
  flat,
  images,
  room,
  actions,
  isPrivate = false,
  statusBadge,
}) => {
  const colors = useColors();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
      }
    },
    [currentIndex],
  );

  const goToNext = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (currentIndex < images.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      }
    },
    [currentIndex, images.length],
  );

  const handleCardClick = () => {
    navigate(`/app/flat-view/${flat.flat_id}`);
  };

  const isAtStart = currentIndex === 0;
  const isAtEnd = currentIndex === images.length - 1;

  const getCurrency = () => {
    try {
      if (flat.location) {
        const parsed = JSON.parse(flat.location) as FlatLocationData;
        return parsed.currency || "CHF";
      }
    } catch {
      return "CHF";
    }
    return "CHF";
  };

  const totalRent = room ? Number(room.rent) + Number(room.utilities || 0) : 0;

  const getLocationCity = () => {
    try {
      if (flat.location) {
        const parsed = JSON.parse(flat.location) as FlatLocationData;
        return parsed.address?.city || parsed.display_name || "";
      }
    } catch {
      return "";
    }
    return "";
  };

  const getFlatDescription = () => {
    if (!flat.flat_text) return "";
    if (typeof flat.flat_text === "string") return flat.flat_text;
    return flat.flat_text.description || "";
  };

  const city = getLocationCity();
  const description = getFlatDescription();

  return (
    <div
      onClick={handleCardClick}
      className="cursor-pointer group overflow-hidden w-full max-w-full min-w-0"
      style={{
        borderRadius: 24,
        backgroundColor: colors.containerNeutralSolidSurface,
        boxShadow:
          "0 12px 48px rgba(2, 2, 16, 0.08), 0 0 0 1px rgba(2, 2, 16, 0.04)",
      }}
    >
      <div className="flex flex-col md:flex-row min-w-0">
        {/* Left — Image */}
        <div className="relative w-full min-w-0 overflow-hidden md:min-w-[200px] md:max-w-[340px] md:basis-[340px]">
          <div className="aspect-[4/3] md:aspect-auto md:h-full">
            {images.length > 0 ? (
              <>
                <div
                  className="flex h-full transition-transform duration-300 ease-out"
                  style={{
                    width: `${images.length * 100}%`,
                    transform: `translateX(-${(currentIndex * 100) / images.length}%)`,
                  }}
                >
                  {images.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`${flat.name} ${index + 1}`}
                      className="h-full object-cover"
                      style={{ width: `${100 / images.length}%` }}
                      draggable={false}
                    />
                  ))}
                </div>

                {/* Private overlay */}
                {isPrivate && (
                  <div
                    className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4"
                    style={{ backgroundColor: "rgba(60, 60, 60, 0.85)" }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="36"
                      height="36"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mb-2"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                    <p className="text-white font-gnarly-semibold text-base">
                      {t("flats_tab.slider.left.private") || "Private"}
                    </p>
                    <p className="text-white text-xs text-center opacity-80 mt-1">
                      {t("flats_tab.slider.left.private_body_help") ||
                        "Edit your flat to make it public"}
                    </p>
                  </div>
                )}

                {/* Carousel nav */}
                {images.length > 1 && !isPrivate && (
                  <>
                    {!isAtStart && (
                      <button
                        onClick={goToPrevious}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-10"
                        style={{
                          backgroundColor: colors.containerNeutralSolidSurface,
                          color: colors.textNeutralDefault,
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </button>
                    )}
                    {!isAtEnd && (
                      <button
                        onClick={goToNext}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110 shadow-lg z-10"
                        style={{
                          backgroundColor: colors.containerNeutralSolidSurface,
                          color: colors.textNeutralDefault,
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    )}
                    <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                      {images.map((_, index) => (
                        <div
                          key={index}
                          className="w-1.5 h-1.5 rounded-full transition-all duration-200"
                          style={{
                            backgroundColor:
                              index === currentIndex
                                ? "white"
                                : "rgba(255, 255, 255, 0.5)",
                            transform:
                              index === currentIndex
                                ? "scale(1.3)"
                                : "scale(1)",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: "rgba(2, 2, 16, 0.04)" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: colors.textNeutralMuted }}
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Right — Details */}
        <div className="flex-1 min-w-0 p-5 flex flex-col">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h2
                className="text-lg font-gnarly-semibold truncate tracking-tight"
                style={{ color: colors.textNeutralDefault }}
              >
                {flat.name}
              </h2>
              {city && (
                <p
                  className="text-sm mt-0.5 truncate"
                  style={{ color: colors.textNeutralMuted }}
                >
                  {city}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {statusBadge ||
                (room && totalRent > 0 ? (
                  <div
                    className="px-3 py-1 rounded-full text-sm font-gnarly-semibold"
                    style={{
                      backgroundColor: colors.containerTertiarySolidLow,
                      color: colors.textTertiaryOnLow,
                    }}
                  >
                    {getCurrency()} {totalRent}/mo
                  </div>
                ) : null)}
            </div>
          </div>

          {/* Detail grid */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-9 mb-4">
            {room && totalRent > 0 && (
              <DetailRow
                colors={colors}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.textNeutralMuted}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                }
                label={t("total_rent") || "Total rent"}
                value={`${getCurrency()} ${totalRent}/mo`}
              />
            )}
            {flat.member_count != null && (
              <DetailRow
                colors={colors}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.textNeutralMuted}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                }
                label={t("flatmates") || "Flatmates"}
                value={`${flat.member_count}`}
              />
            )}
            {room?.size && (
              <DetailRow
                colors={colors}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.textNeutralMuted}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  </svg>
                }
                label={t("room_size") || "Room size"}
                value={`${room.size} m²`}
              />
            )}
            {flat.size && Number(flat.size) > 0 && (
              <DetailRow
                colors={colors}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.textNeutralMuted}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                }
                label={t("flat_size") || "Flat size"}
                value={`${flat.size} m²`}
              />
            )}
            {room?.furnishing && (
              <DetailRow
                colors={colors}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.textNeutralMuted}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3" />
                    <path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H6v-2a2 2 0 0 0-4 0Z" />
                    <path d="M4 18v2" />
                    <path d="M20 18v2" />
                  </svg>
                }
                label={t("furnishing") || "Furnishing"}
                value={t(room.furnishing) || room.furnishing}
              />
            )}
            {room?.from_date && (
              <DetailRow
                colors={colors}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={colors.textNeutralMuted}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                }
                label={t("available_from") || "Available from"}
                value={new Date(room.from_date).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              />
            )}
          </div>

          {/* Description preview */}
          {description && (
            <p
              className="text-sm leading-relaxed line-clamp-2 mt-3 mb-4"
              style={{ color: colors.textNeutralMuted }}
            >
              {description}
            </p>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-auto">
            {actions.map((action, idx) => (
              <button
                key={idx}
                onClick={action.onClick}
                className="rounded-full px-4 py-2 text-xs font-gnarly-semibold transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] flex items-center gap-1.5"
                style={{
                  backgroundColor: action.primary
                    ? colors.accentColor
                    : "rgba(2, 2, 16, 0.05)",
                  color: action.primary ? "white" : colors.textNeutralDefault,
                }}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlatDetailCard;
export type {
  FlatCardData,
  FlatRoomData,
  FlatLocationData,
  FlatTextData,
  ActionConfig,
};
